import {Component, ElementRef, EventEmitter, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import {StudentList} from '../student-list.model';
import {Student} from '../../../shared-components/student/student.model';
import {TylerMatTableDataSource} from '../../../shared-components/angular-material/extended.class';
import {LoginService} from '../../../shared-modules/login/login.service';
import {GeneralPreferences} from '../../../shared-components/user-preferences/general-preferences.model';
import {UserPreferencesService} from '../../../shared-components/user-preferences/user-preferences.service';
import {System} from '../../../shared-components/System';
import {Subscription} from 'rxjs';
import {StudentListsService} from '../student-lists.service';
import {ErrorList} from '../../../shared-components/controls/error-prompt/error-list.model';
import {NgForm} from '@angular/forms';
import {SharedService} from '../../../shared-components/shared.service';
import {LookupService} from '../../../shared-components/lookup/lookup.service';
import {Lookup} from '../../../shared-components/lookup/lookup.model';
import { SelectedSitesDialogComponent } from '../../../shared-components/controls/selected-sites-dialog/selected-sites-dialog.component';
import {UserService} from '../../../shared-components/user/user.service';
import {Staff} from '../../../shared-components/staff-search/staff.model';
import * as _ from 'lodash';
import {SecurityService} from '../../../shared-components/security/security.service';
import {take} from 'rxjs/operators';
import {StaffSearchService} from '../../../shared-components/staff-search/staff-search.service';
import {YearService} from '../../../shared-components/year/year.service';
import { SelectedSite } from '../../../shared-components/controls/selected-sites-dialog/selected-site';
import {StaffSearchComponent} from '../../../shared-components/staff-search/staff-search.component';

@Component({
	selector: 'app-add-edit-student-list-dialog',
	templateUrl: './add-edit-student-list-dialog.component.html',
	styleUrls: ['./add-edit-student-list-dialog.component.css']
})
export class AddEditStudentListDialogComponent implements OnInit, OnDestroy {

	// Injected Properties
	editMode = false;
	addMode = false;
	injectedStudents: Student[] = [];
	studentList = new StudentList();
	disableStudents = false;
    allowCreateDistrictLists = false;
    allowCreateSchoolLists = false;
    staffId: number;
    staff: Staff[];
    admin: boolean;

	// Interface properties
	studentEmitter = new EventEmitter<Student[]>();
	searchEmitter = new EventEmitter<any>();

	// Properties
	originalList = new StudentList();
	V10: boolean;
	generalPreferences: GeneralPreferences;
	subscriptions: Subscription[] = [];
	initialStudents: Student[] = [];
	errors: ErrorList[] = [];
	@ViewChild('nameField', { read: ElementRef, static: true }) nameField: ElementRef;
	@ViewChild('descriptionField', { read: ElementRef, static: true }) descriptionField: ElementRef;
	@ViewChild('form', { read: NgForm, static: true }) form: NgForm;
	types: Lookup[] = [];
	scopes: Lookup[] = [];
	ownerNotInStaff = false;
	initialOwnerId = 0;

	// Table Properties
	tableData = new TylerMatTableDataSource();
	private _sort: MatSort;

	// Class Functions
	constructor(
		private dialogBox: MatDialogRef<AddEditStudentListDialogComponent>,
		@Inject(MAT_DIALOG_DATA) private data: any,
		private dialog: MatDialog,
		private loginService: LoginService,
		private userPreferencesService: UserPreferencesService,
		private studentListsService: StudentListsService,
		private sharedService: SharedService,
		private lookupService: LookupService,
        private userService: UserService,
        private securityService: SecurityService,
        private staffSearchService: StaffSearchService,
        private yearService: YearService
	) { }

	get sort(): MatSort {
		return this._sort;
	}

	@ViewChild(MatSort, { static: true }) set sort(value: MatSort) {
		this._sort = value;
		this.tableData.sort = value;
	}

	get viewMode() {
		return !this.editMode && !this.addMode;
	}

	// Event Functions
	ngOnInit() {
		this.V10 = this.loginService.LoginSettings.V10;
		this.generalPreferences = this.userPreferencesService.generalPreferences;

		// Helper for handling properties
        let currentOwner: Staff;
        const handleStaff = (staff: Staff[]) => {
            this.staff = _.cloneDeep(staff);
            this.staff.sort((a, b) => {
                if (a.LastName.localeCompare(b.LastName) != 0) {
                    return a.LastName.trim().localeCompare(b.LastName.trim());
                } else {
                    return a.FirstName.trim().localeCompare(b.FirstName.trim());
                }
            });
            this.ownerNotInStaff = true;
            this.staff.forEach(staffMember => {
                if (staffMember.StaffId == this.staffId) {
                    this.ownerNotInStaff = false;
                }
			});

			// For all new lists, the owner should be defaulted to the current user
			if (this.studentList.ID <= 0) {
				this.studentList.Owner = this.staffId;
				this.studentList.OwnerFirstName = this.userService.currentUser.FirstName;
				this.studentList.OwnerMiddleName = this.userService.currentUser.MiddleName;
				this.studentList.OwnerLastName = this.userService.currentUser.LastName;
			}

            // If the staff does not contain the owner of the list, add that to the list so it can show and be reverted
            if ((this.editMode && this.V10) || (!this.V10)) {
                currentOwner = this.staff.find(o => o.StaffId == this.studentList.Owner);
                if (currentOwner == null && this.staffId > 0) {
                    currentOwner = new Staff();
                    currentOwner.StaffId = this.studentList.Owner;
                    currentOwner.FirstName = this.studentList.OwnerFirstName;
                    currentOwner.MiddleName = this.studentList.OwnerMiddleName;
                    currentOwner.LastName = this.studentList.OwnerLastName;
                    this.staff.push(currentOwner);
                    this.staff.sort((a: Staff, b: Staff) => {
                        const lastNameCompare = a.LastName.localeCompare(b.LastName);
                        const firstNameCompare = a.FirstName.localeCompare(b.FirstName);
                        if (lastNameCompare != 0) {
                            return lastNameCompare;
                        } else {
                            return firstNameCompare;
                        }
                    });
                }
            }
        };

		// Setting injected properties
		if (this.data) {
			this.editMode = this.data.editMode;
			this.addMode = this.data.addMode;
			this.injectedStudents = this.data.students;
			this.disableStudents = this.data.disableStudents;
			if (this.data.studentList) {
				this.studentList = this.data.studentList.duplicate();
                this.initialOwnerId = this.studentList.Owner;
			}
			this.allowCreateDistrictLists = this.data.allowCreateDistrictLists;
			this.allowCreateSchoolLists = this.data.allowCreateSchoolLists;
			this.staffId = this.data.staffId;
			if (this.data.staff) {
                handleStaff(this.data.staff);
            }
			this.admin = this.data.admin;
		}

		// Obtaining staff and staffId if it is not provided
        // NOTE: we must check undefined here because admins deliberately use 0
        if (this.staffId == undefined) {
            this.loginService.getStaffIdByUserId().pipe(take(1)).subscribe(staffId => {
                this.staffId = staffId;
                if (this.studentList.ID <= 0) {
                    this.studentList.Owner = this.staffId;
                }
            });
        }
        if (!this.staff || this.staff.length == 0) {
            this.staffSearchService.getStaffSearch(
                -1,
                this.yearService.defaultYear ? this.yearService.defaultYear.Id : this.yearService.loginYear.Id,
                true,
                '',
                '',
                '',
                this.yearService.schools.map(o => o.Id),
                '',
                '',
                '',
                '',
                '',
                '',
                '',
                '',
                '',
                '',
                '',
                '',
                '',
                true
            ).pipe(take(1)).subscribe(
                staff => {
                    handleStaff(staff);
                }
            );
        }

		// Checking permission properties if they were not injected
        const permissions = this.securityService.userPermissions;
        permissions.forEach(permission => {
            switch (permission.PrivilegeId) {
                case 2230:
                    this.allowCreateDistrictLists = true;
                    break;
                case 2229:
                    this.allowCreateSchoolLists = true;
                    break;
                default:
                    break;
            }
        });

		// Setting up interface
		this.subscriptions.push(
			this.studentEmitter.subscribe(students => {
				if (students) {
					for (let i = 0; i < students.length; i++) {
						students[i] = System.convert(students[i], new Student());
					}
					this.studentList.Students = students;
					this.tableData.data = this.studentList.Students;
				}
			})
		);

		// Obtaining types and scopes
		this.subscriptions.push(
			this.lookupService.getStudentListScopes().subscribe(scopes => {
				this.scopes = scopes;
			}),
			this.lookupService.getStudentListTypes().subscribe(types => {
				this.types = types;
			})
		);

		// Setting initial student list properties
		Object.assign(this.originalList, this.studentList);	// We must do this before we mess with the student list at all
		if (this.addMode) {
			this.studentList.Students = this.injectedStudents;
		}
		if (this.studentList.Students && this.studentList.Students.length > 0) {
			for (let i = 0; i < this.studentList.Students.length; i++) {
				this.initialStudents.push(this.studentList.Students[i]);
			}
		} else if (!this.addMode) {
			this.studentList.Students = [];
			this.subscriptions.push(
				this.studentListsService.getStudentList(this.studentList.ID).subscribe(studentList => {
					if (studentList) {
						if (studentList.Students) {
							this.studentList.Students = studentList.Students;
							for (let i = 0; i < this.studentList.Students.length; i++) {
								this.studentList.Students[i] = System.convert(this.studentList.Students[i], new Student());
							}
							this.initialStudents = this.studentList.Students;
							this.tableData.data = this.studentList.Students;
						}
						if (studentList.SchoolIds) {
                            this.studentList.SchoolIds = studentList.SchoolIds;
                        }
					} else {
						this.sharedService.showBadSnackbar('Data failed to load');
					}
				})
			);
		}
		if (!this.studentList.Access) {
		    this.studentList.Access = 'Private';
        }

		// Setting up student table
		this.tableData.data = this.studentList.Students;
		this.tableData.sortingDataAccessor = (data: Student, sortHeaderId: string): string|number => {
			switch (sortHeaderId) {
				case 'number':
					return data.StudentNumber;
				case 'name':
					return data.formattedName(this.generalPreferences);
				default:
					if (data[sortHeaderId] instanceof Date) {
						return data[sortHeaderId].getTime();
					} else {
						return this.tableData.defaultSortingAccessor(data, sortHeaderId);
					}
			}
		};
	}

	ngOnDestroy(): void {
		this.subscriptions.forEach(subscription => {
			subscription.unsubscribe();
		});
	}

	save() {
		// PRE: the save button is clicked
		// POST: validates the data. If valid, saves.
		this.validate();
		if (this.V10 == false && this.studentList.Owner == 0) {
			this.studentList.Owner = this.staff.find(o => o.FirstName == 'sdmadmin' && o.LastName == 'sdmadmin').StaffId;
		}
		if (this.errors.length == 0) {
			this.subscriptions.push(
				this.studentListsService.saveStudentList(this.studentList).subscribe(id => {
					if (id < 0) {
						this.sharedService.showBadSnackbar();
					} else {
						this.studentList.ID = id;
						const owner = this.staff.find(o => o.StaffId == this.studentList.Owner);
                        if (owner != null) {
                            this.studentList.OwnerLastName = owner.LastName;
                            this.studentList.OwnerFirstName = owner.FirstName;
                            this.studentList.OwnerMiddleName = owner.MiddleName;
                        }
						this.dialogBox.close(this.studentList);
						this.sharedService.showGoodSnackbar();
					}
				})
			);
		}
	}

	cancel() {
		// PRE: the cancel or close button is clicked
		// POST: resets the student list and closes the dialog box
		Object.assign(this.studentList, this.originalList);
		this.studentList.Students = this.initialStudents;
		this.dialogBox.close();
	}

	selectedSitesClick() {
		// PRE: the selected sites link is clicked
		// POST: opens the selected sites
		const selectedSites: SelectedSite[] = [];
		if (this.studentList.SchoolIds != null) {
			this.studentList.SchoolIds.forEach(o => {
				const ss = new SelectedSite();
				ss.Id = o;
				selectedSites.push(ss);
			});
		}

        // Opening the modal
		const selectedSitesDialogBox = this.dialog.open(SelectedSitesDialogComponent, {
			disableClose: true,
			width: '700px',
			data: {
				selectedSites: selectedSites,
                viewOnly: this.viewMode || !this.allowCreateSchoolLists
			}
		});
		selectedSitesDialogBox.afterClosed().subscribe((results: SelectedSite[]) => {
			if (results) {
				this.studentList.SchoolIds = results.map(o => o.Id);
			}
			// console.log(this.studentList);
		});
	}

	deleteStudent(student: Student) {
		// PRE: a student's delete link is clicked
		// POST: removes the student from the student list
		if (!this.viewMode && !this.disableStudents) {
			this.studentList.Students.splice(this.studentList.Students.indexOf(student), 1);
			this.tableData.data = this.studentList.Students;
		}
	}

	changeStudents() {
		// PRE: the change student button is clicked
		// POST: opens the student search dialog to change the selected students
		this.searchEmitter.emit({
			performRoute: false,
			returnSelectedStudents: true,
			searchAndSelect: true,
			selectedStudents: this.studentList.Students,
            creatingStudentList: true
		});
	}

	selectStaff() {
	    // PRE: the SELECT STAFF button is clicked
        // POST: opens the staff search dialog to change the owner
        if (!this.viewMode) {
            const dialogBox = this.dialog.open(StaffSearchComponent, {
                disableClose: true,
                maxWidth: '100vw',
                maxHeight: '100vh',
                width: '100vw',
                height: '100vh',
                data: {
                    searchAndSelect: false
                }
            });
            dialogBox.afterClosed().pipe(take(1)).subscribe((staff: Staff) => {
                if (staff) {
                    this.studentList.Owner = staff.StaffId;
                    this.studentList.OwnerFirstName = staff.FirstName;
                    this.studentList.OwnerMiddleName = staff.MiddleName;
                    this.studentList.OwnerLastName = staff.LastName;
                }
            });
        }
    }

	// Methods
    getHint(currentValue: string, maxLength: number) {
	    return (maxLength - (currentValue ? currentValue.length : 0)).toString() + ' characters left';
    }

    getDisabled() {
	    return this.viewMode || this.staffId != this.studentList.Owner;
    }

	validate() {
		// POST: sets up the errors

		// Resetting errors
		this.errors = [];

		// Triggering field validation
		System.forceUpdateValidity(this.form);

		// Name is required
		if (!this.studentList.Name) {
			this.errors.push({
				fieldName: 'Name',
				fieldRef: this.nameField,
				errorMsg: 'Required field.'
			});
		}

		// Description is required
		if (!this.studentList.Description) {
			this.errors.push({
				fieldName: 'Description',
				fieldRef: this.descriptionField,
				errorMsg: 'Required field.'
			});
		}

		// Status is required
		if (this.studentList.Status == null) {
			const element = new ElementRef(document.getElementById('statusActiveRadio'));
			this.errors.push({
				fieldName: 'Status',
				fieldRef: element,
				errorMsg: 'Required field.'
			});
		}

		// Access is required
		if (!this.studentList.Access) {
			const element = new ElementRef(document.getElementById('accessPrivateRadio'));
			this.errors.push({
				fieldName: 'Access',
				fieldRef: element,
				errorMsg: 'Required field.'
			});
		}

		// If access is public within school, sites are required
		if (this.studentList.Access == 'Public within School' &&
			(!this.studentList.SchoolIds || this.studentList.SchoolIds.length < 1)) {
			this.errors.push({
				fieldName: 'Selected Sites',
				fieldRef: null,
				errorMsg: 'A site must be selected if the list is public within school.'
			});
		}

		// At least one student must be present
		if (!this.studentList.Students || this.studentList.Students.length < 1) {
			this.errors.push({
				fieldName: 'Students',
				fieldRef: null,
				errorMsg: 'At least one student must be a part of the list.'
			});
		}
	}
}
